import React, { CSSProperties } from 'react';
import { useReduxState } from 'hooks';
import ReactDOM from 'react-dom';
import ReactHintFactory from 'react-hint';
import {
  Cell,
  Label,
  Pie,
  PieChart as RechartsPieChart,
  ResponsiveContainer
} from 'recharts';

const ReactHint = ReactHintFactory(React);

export type PieChartData = {
  name: string;
  value: number;
  tooltipValue: string;
  color: string;
}[];

interface Props {
  accessibilityLayer?: boolean;
  data: PieChartData;
  dataKey?: string;
  disableAnimation?: boolean;
  emptyColor?: string;
  emptyTooltip: string;
  nameKey?: string;
  height?: number;
  id: string;
  innerRadius?: string | number;
  label?: string;
  showTooltip?: boolean;
  style?: CSSProperties;
  strokeWidth?: number;
}

const PieChart = ({
  accessibilityLayer = false,
  data,
  dataKey = 'value',
  disableAnimation = false,
  emptyColor,
  emptyTooltip,
  nameKey = 'name',
  height = 100,
  id,
  innerRadius = '75%',
  label = null,
  showTooltip,
  style = {},
  strokeWidth = 1
}: Props) => {
  const { theme } = useReduxState();

  return (
    <>
      {showTooltip && (data.find((item) => item.value > 0) || emptyTooltip)
        ? ReactDOM.createPortal(
            <ReactHint
              attribute={`data-pie-chart-tooltip-${id}`}
              autoPosition={true}
              className="vuo-tooltip-alt"
              events={true}
              delay={50}
              onRenderContent={() => (
                <div className="vuo-tooltip__content">
                  {data.find((item) => item.value > 0) ? (
                    data.map((item) => (
                      <div
                        className="item"
                        key={item.name}
                        style={{ padding: 3 }}
                      >
                        <div
                          className="icon"
                          style={{
                            backgroundColor: item.color || theme.chartMainColor,
                            borderRadius: 20,
                            display: 'inline-block',
                            marginRight: 5,
                            position: 'relative',
                            top: -1,
                            height: 8,
                            width: 8
                          }}
                        />
                        {`${item.name}: ${item.tooltipValue || item.value}`}
                      </div>
                    ))
                  ) : (
                    <div style={{ padding: 3 }}>{emptyTooltip}</div>
                  )}
                </div>
              )}
            />,
            document.querySelector('.dynamic-tooltip-root')
          )
        : null}
      <div
        className="pie"
        style={{ width: '100%', height, ...style }}
        {...(showTooltip &&
        (data.find((item) => item.value > 0) || emptyTooltip)
          ? {
              [`data-pie-chart-tooltip-${id}`]: true,
              'aria-label': data
                .map(
                  (item) => `${item.name}: ${item.tooltipValue || item.value}`
                )
                .join(', ')
            }
          : {})}
      >
        <ResponsiveContainer>
          <RechartsPieChart accessibilityLayer={accessibilityLayer}>
            <Pie
              data={[
                ...data,
                ...(data.find((item) => item.value > 0)
                  ? []
                  : [{ name: 'empty', value: 1 }])
              ]}
              dataKey={dataKey}
              endAngle={-270}
              nameKey={nameKey}
              innerRadius={innerRadius}
              isAnimationActive={!disableAnimation}
              outerRadius={'100%'}
              fill={theme.chartMainColor}
              startAngle={90}
              strokeWidth={strokeWidth}
            >
              {data.map((entry, i) => (
                <Cell
                  key={`cell-${i}`}
                  fill={entry.color || theme.chartMainColor}
                />
              ))}
              <Cell fill={emptyColor || theme.lightGrey} />
              {label ? <Label position="center" value={label} /> : null}
            </Pie>
          </RechartsPieChart>
        </ResponsiveContainer>

        <style jsx>{`
          .pie :global(.recharts-label) {
            fill: ${theme.textColorLight};
            font-size: 1.6rem;
            font-weight: 700;
          }
        `}</style>
      </div>
    </>
  );
};

export default PieChart;
